import React, { useMemo, useRef, useState } from "react";

import { Button, Dialog, Grid } from "@mui/material";

import { BlockStandard } from "components/admin/BlockStandard";
import { PageStandard } from "components/admin/PageStandard";
import { deleteBuilding, patchBuilding } from "api/onboarding";
import { useUserContext } from "contexts/UserContext";
import { CREATE_BUILDING } from "routers/routePaths";
import {
  useSetError,
  useErrorInfo,
  useClearError,
} from "contexts/ErrorContext";
import { CustomCodes, ErrorResponse } from "contexts/ErrorContextData";
import { FileUpload } from "components/MUIExtensions/FileUpload";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";
import { TextWithConfirmation } from "components/MUIExtensions/inputs/TextWithConfirmation";
import { BUILDING_STRUCTURES } from "./BuildingStructuresData";
import { SubConceptButton } from "components/inputs/SubConceptButton";
import { ButtonGrid } from "components/layout/ButtonGrid";
import { BuildingDetail } from "components/forms/BuildingDetail";

export const BuildingEdit = ({ history }) => {
  const { currentSpace, token } = useUserContext();
  const nameRef = useRef();
  const infoRef = useRef();
  const setError = useSetError();
  const errorInfo = useErrorInfo();
  const clearError = useClearError();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [displayModal, setDisplayModal] = useState<string>("");
  const name = useMemo(() => currentSpace?.schoolName || "", [currentSpace]);
  const [newImg, setNewImg] = useState<HTMLInputElement["files"] | null>(null);

  const startValues = useMemo(() => {
    if (!currentSpace?.properties[0]) {
      return { propertyName: name || "" };
    } else {
      const buildingProperties = currentSpace.properties[0];

      return {
        propertyName: buildingProperties.propertyName,
        city: buildingProperties.city,
        propertyType: buildingProperties.propertyType,
      };
    }
  }, [currentSpace, name]);

  const handleAdd = () => {
    history.push(CREATE_BUILDING);
  };

  const handleSaveInfo = () => {
    // @ts-expect-error custom ref functions are not recognized by TS
    if (infoRef.current?.isFormValid()) {
      // @ts-expect-error custom ref functions are not recognized by TS
      const formValues = infoRef.current?.getFormValues();
      editBuilding(formValues);
      setDisplayModal("");
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const editBuilding = async (patchFields) => {
    setIsLoading(true);

    try {
      await patchBuilding(token, currentSpace?.id || "", patchFields);
    } catch (error) {
      setError(error as ErrorResponse);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (currentSpace?.id) {
      try {
        await deleteBuilding(token, currentSpace.id);
      } catch (error) {
        setError(error as ErrorResponse);
      }
    } else {
      setError(CustomCodes.INTERNAL_PWA_ERROR);
    }
  };

  const handleFileUpload = (file) => {
    if (file) {
      setNewImg(file);
      editBuilding({ buildingImage: file });
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const handleNameUpdate = (newName) => {
    editBuilding({ propertyName: newName });
    // @ts-expect-error custom ref functions are not recognized by TS
    nameRef.current?.valueWasSaved();
  };

  const handleModalClick = (concept: string) => {
    setDisplayModal(concept);
  };

  const handleModalClose = () => {
    setDisplayModal("");
    if (errorInfo?.code === CustomCodes.INCOMPLETE_FORM) {
      clearError();
    }
  };

  return (
    <PageStandard title="Edição do imóvel" displaySelector onAdd={handleAdd}>
      <BlockStandard hideDivider>
        <TextWithConfirmation
          ref={nameRef}
          startValue={name}
          label="Nome"
          onSave={handleNameUpdate}
          endIcon="edit"
          isLoading={isLoading}
          required
        />
      </BlockStandard>
      <BlockStandard hideDivider>
        <FileUpload
          fullWidth
          currentFile={newImg || undefined}
          fileSrc={
            !newImg && currentSpace?.properties[0]?.buildingImagePath
              ? currentSpace?.properties[0].buildingImagePath
              : ""
          }
          text="Alterar imagem"
          onFileUpload={handleFileUpload}
          hasError={
            errorInfo &&
            !(newImg?.length || currentSpace?.properties[0]?.buildingImagePath)
          }
        />
      </BlockStandard>

      <ButtonGrid>
        {BUILDING_STRUCTURES.map((item) => {
          if (item.action === "click") {
            return (
              <SubConceptButton
                key={item.label}
                {...item}
                onClick={handleModalClick}
              />
            );
          } else {
            return <SubConceptButton key={item.label} {...item} />;
          }
        })}
      </ButtonGrid>

      <DeleteCTA onDeleteConfirmed={handleDelete} />

      <Dialog
        open={displayModal === BUILDING_STRUCTURES[0].label}
        onClose={handleModalClose}
      >
        <Grid
          item
          container
          direction="column"
          gap={3}
          padding={4}
          sx={{ minWidth: "400px" }}
        >
          <BuildingDetail ref={infoRef} startValues={startValues} />
          <Button variant="contained" onClick={handleSaveInfo}>
            Salvar modificações
          </Button>
        </Grid>
      </Dialog>
    </PageStandard>
  );
};
